import dayjs from 'dayjs';
import { createContext, useContext, useState, type ReactNode } from 'react';
import type { GetDatevVerbindungenQuery } from '../datev-service/gql/graphql';
import { useDatevVerbindungen } from '../features/DatevAuthStatus/hooks/useDatevVerbindungen';
import {
  datevServiceLohnabrechnungsart,
  type Lohnabrechnungsart,
} from '../features/Login/constants/lohnabrechnungsart';
import type { Lohnabrechnungseinstellungen } from '../hooks/useStandort/entities/Lohnabrechnungseinstellungen';
import { useStandortContext } from './StandortContext';

type DatevAuthStatus = {
  authorized: boolean;
  uid: string;
  expiresAt: string;
  beraternummer: string;
  mandantennummer: string;
} | null;

type DatevAuthContextValue = {
  status: DatevAuthStatus;
  loading: boolean;
  logout: () => void;
};

const DatevAuthContext = createContext<DatevAuthContextValue | undefined>(
  undefined,
);

type Verbindung = NonNullable<
  NonNullable<GetDatevVerbindungenQuery['datevVerbindungen']>[number]
>;

/** Prefers the connection matching the Standort's settings, else any non-expired one. */
function findAktiveVerbindung(
  verbindungen: GetDatevVerbindungenQuery['datevVerbindungen'] | undefined,
  einstellungen?: Lohnabrechnungseinstellungen,
): Verbindung | undefined {
  const aktive = (verbindungen ?? []).filter(
    (v): v is Verbindung =>
      v != null && dayjs(v.refreshTokenExpiresAt).isAfter(dayjs()),
  );
  return (
    aktive.find(
      (v) =>
        v.beraternummer === einstellungen?.beraternummer &&
        v.mandantennummer === einstellungen?.mandantennummer &&
        v.lohnabrechnungsart ===
          datevServiceLohnabrechnungsart[
            einstellungen?.lohnabrechnungsart as Lohnabrechnungsart
          ],
    ) ?? aktive[0]
  );
}

export function DatevAuthProvider({ children }: { children: ReactNode }) {
  const { standort } = useStandortContext();
  const [loggedOutLocally, setLoggedOutLocally] = useState(false);

  const { verbindungen, loading } = useDatevVerbindungen();

  const verbindung = findAktiveVerbindung(
    verbindungen,
    standort?.lohnabrechnungseinstellungen,
  );

  const status: DatevAuthStatus =
    verbindung && !loggedOutLocally
      ? {
          authorized: true,
          uid: verbindung.uid,
          expiresAt: verbindung.refreshTokenExpiresAt,
          beraternummer: verbindung.beraternummer,
          mandantennummer: verbindung.mandantennummer,
        }
      : null;

  return (
    <DatevAuthContext.Provider
      value={{
        status,
        loading,
        // TODO: Replace by a logout mutation once the DATEV service offers one.
        logout: () => setLoggedOutLocally(true),
      }}>
      {children}
    </DatevAuthContext.Provider>
  );
}

// oxlint-disable-next-line react/only-export-components
export function useDatevAuthContext(): DatevAuthContextValue {
  const context = useContext(DatevAuthContext);

  if (context === undefined) {
    throw new Error(
      'useDatevAuthContext must be used within a DatevAuthProvider',
    );
  }

  return context;
}
